import { useAuth } from "components/Auth";
import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "redux/hooks";

import * as s from "./SubStyled";

import { IoIosCopy, IoMdSettings } from "react-icons/io";
import { COLORS } from "styles/colors";
import { handleCopyClipBoard } from "utils/functions";
import { ProfileAccountInfo } from "api/interfaces/accountInterface.interface";

import { PiRecordFill, PiSignOut } from "react-icons/pi";
import { BiHelpCircle } from "react-icons/bi";
import { useMenu } from "hook/useMenu";
import { useProSidebar } from "react-pro-sidebar";
import SwitchOrgDropdown from "./SwitchOrgDropdown";

const SCREEN_RECORDER_SUPPORTED =
  !!window.navigator &&
  !!window.navigator.mediaDevices &&
  !!window.navigator.mediaDevices.getDisplayMedia;

interface MenuItem {
  label: string;
  icon: JSX.Element;
  disabled?: boolean;
  onClick: () => void;
  visible: boolean;
}

interface Props {
  onConfirmScreenRecording: () => void;
}

export const iconSize = "20px";

const UserPopup = forwardRef((props: Props, ref: any): JSX.Element => {
  const navigate = useNavigate();
  const auth = useAuth();
  const { menuNavigation } = useMenu({});
  const [isSwitchAccount, setIsSwitchAccount] = useState<boolean>(false);

  const [isHiddenRecording, setIsHiddenRecording] = useState<boolean>(false);

  const dispatch = useAppDispatch();
  const [totalAccount, setTotalAccount] = useState<number>(
    auth.user.totalAccount
  );

  const { collapseSidebar, collapsed } = useProSidebar();
  const selectedAccount: ProfileAccountInfo = useAppSelector(
    (state) => state.accountSettings
  );

  useEffect(() => {
    setTotalAccount(auth.user.totalAccount);
  }, [auth.user.totalAccount]);

  const onClickLoutout = useCallback(() => {
    /**
     * @todo clear redux store
     */
    auth.logout();
    // navigate("/login", { replace: true });
  }, [auth, navigate]);

  const menus: MenuItem[] = useMemo(
    () =>
      [
        {
          label: "Profile Settings",
          icon: <IoMdSettings size={iconSize} />,
          onClick: () => {
            menuNavigation({ to: "/settings/myaccount" });
          },
          visible: true,
        },
        {
          label: "Screen Recording",
          icon: <PiRecordFill size={iconSize} />,
          disabled: isHiddenRecording,
          onClick: props.onConfirmScreenRecording,
          visible: SCREEN_RECORDER_SUPPORTED && selectedAccount.isEnabled,
        },
        {
          label: "Help",
          icon: <BiHelpCircle size={iconSize} />,
          onClick: () => {
            menuNavigation({ to: "/help/videoguides" });
            //navigate("/help/videoguides");
          },
          visible: true,
        },
        {
          label: "Sign Out",
          icon: <PiSignOut size={iconSize} />,
          onClick: onClickLoutout,
          visible: true,
        },
      ].filter((menu) =>
        (isHiddenRecording && menu.label === "Screen Recording") ||
        !menu.visible
          ? false
          : true
      ),
    [totalAccount, isHiddenRecording, selectedAccount]
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia("(pointer: coarse)");
    const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");
    const handleTouchChange = (e: MediaQueryListEvent | MediaQueryList) => {
      if (isFirefox || e.matches) {
        setIsHiddenRecording(true);
      }
    };

    handleTouchChange(mediaQuery);
    mediaQuery.addEventListener("change", handleTouchChange);
    return () => {
      mediaQuery.removeEventListener("change", handleTouchChange);
    };
  }, []);

  return (
    <>
      <s.PopupWrapper key={"userPopupWrapper"} ref={ref}>
        <s.AccountId>
          <span>Organization ID</span>
          <span>
            {selectedAccount.accountNumber}{" "}
            <button
              onClick={(e) => {
                // e.preventDefault();
                e.stopPropagation();
                handleCopyClipBoard(selectedAccount.accountNumber);
              }}
              data-testid="blocks-headernav-sub-user-popup-orgid-copy"
            >
              <IoIosCopy color={COLORS.BORDER} />
            </button>
          </span>
        </s.AccountId>
        {auth.user.totalAccount > 1 && (
          <SwitchOrgDropdown
            setIsSwitchAccount={setIsSwitchAccount}
            isSwitchAccount={isSwitchAccount}
          />
        )}
        {menus.map((menu, index) => {
          return (
            <Fragment key={index}>
              {index === menus.length - 1 && <s.Line></s.Line>}
              <s.PopupMenu
                disabled={menu.disabled || false}
                key={menu.label}
                onClick={(e) => {
                  if (menu.disabled) {
                    e.stopPropagation();
                    return;
                  }
                  menu.onClick();
                  if (!collapsed) collapseSidebar();
                }}
                data-testid={`blocks-headernav-sub-user-popup-menu-${(
                  menu.label ?? ""
                )
                  .toLowerCase()
                  .replace(" ", "-")}`}
              >
                {menu.icon}
                {menu.label}
              </s.PopupMenu>
            </Fragment>
          );
        })}
      </s.PopupWrapper>
    </>
  );
});

export default UserPopup;
